// Customer Mobile App Phase 2 (Sep 2026) — Stripe "bounce" page for the
// tenant-branded customer app.
//
//   Stripe Checkout (opened in the app's secure in-app browser)
//     -> https://<canonical web>/retail/:slug/app-return?result=success|cancel&payment_id=...&app=<app_key>
//     -> this page hands off to <registered scheme>://payment-return?...
//     -> the in-app browser closes and the APP asks the backend for the
//        authoritative payment status.
//
// This page NEVER decides whether a payment succeeded and never shows
// "paid": the success/cancel value is a navigation hint only. The app
// scheme comes from the tenant's registered app (`/app-config?app=`),
// never from this URL, so the page cannot be used as an open redirect.
// Additive: no existing route or component is changed.
import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const RETAIL_BASE = `${BACKEND_URL}/api/retail/public`;
const SCHEME_RE = /^[a-z][a-z0-9+.-]{2,40}$/;
const SAFE_PARAM_RE = /^[A-Za-z0-9_-]{1,200}$/;

export function buildAppReturnLink(scheme, params) {
  if (!scheme || !SCHEME_RE.test(scheme)) return null;
  const qs = new URLSearchParams();
  qs.set("result", params.result === "success" ? "success" : "cancel");
  for (const key of ["payment_id", "session_id", "link_slug"]) {
    const v = params[key];
    if (v && SAFE_PARAM_RE.test(v)) qs.set(key, v);
  }
  return `${scheme}://payment-return?${qs.toString()}`;
}

export default function RetailAppReturn() {
  const { slug } = useParams();
  const [search] = useSearchParams();
  const [scheme, setScheme] = useState(null);
  const [lookupDone, setLookupDone] = useState(false);

  const params = useMemo(() => ({
    result: search.get("result") || "cancel",
    payment_id: search.get("payment_id") || "",
    session_id: search.get("session_id") || "",
    link_slug: search.get("link_slug") || "",
    app: (search.get("app") || "").toLowerCase(),
  }), [search]);

  useEffect(() => {
    let cancelled = false;
    if (!params.app || !SAFE_PARAM_RE.test(params.app)) {
      setLookupDone(true);
      return undefined;
    }
    axios.get(`${RETAIL_BASE}/${encodeURIComponent(slug)}/app-config`, { params: { app: params.app } })
      .then((r) => {
        if (cancelled) return;
        const app = (r.data && r.data.app) || {};
        setScheme(app.registered ? app.scheme : null);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLookupDone(true); });
    return () => { cancelled = true; };
  }, [slug, params.app]);

  const deepLink = buildAppReturnLink(scheme, params);

  useEffect(() => {
    if (deepLink) {
      try { window.location.replace(deepLink); } catch (_e) { /* fall back to the button */ }
    }
  }, [deepLink]);

  const storefront = `/retail/${encodeURIComponent(slug)}${params.link_slug && SAFE_PARAM_RE.test(params.link_slug) ? `/${params.link_slug}` : ""}`;

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4" data-testid="retail-app-return">
      <div className="max-w-sm w-full bg-white rounded-xl shadow p-6 text-center space-y-4">
        <h1 className="text-lg font-semibold text-slate-900">
          {params.result === "success" ? "Returning to the app…" : "Payment not completed"}
        </h1>
        <p className="text-sm text-slate-600">
          {params.result === "success"
            ? "The app will confirm your payment status with the restaurant."
            : "No payment was taken. You can return to the app to try again."}
        </p>
        {deepLink ? (
          <a href={deepLink} className="block w-full rounded-lg bg-slate-900 text-white py-3 font-semibold"
             data-testid="retail-app-return-open">
            Open the app
          </a>
        ) : (
          lookupDone && <p className="text-sm text-slate-500" data-testid="retail-app-return-noapp">
            If the app doesn't open, return to it from your home screen.
          </p>
        )}
        <Link to={storefront} className="block text-sm text-slate-600 underline" data-testid="retail-app-return-web">
          Continue in the browser instead
        </Link>
      </div>
    </div>
  );
}
